import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { GOOGLE } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  TOP_KEYWORDS,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  AD,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_TITLE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_DESCRIPTION,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_ASSETS_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_VIDEOS,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_IMAGES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_HEADLINES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_LONG_HEADLINES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_DESCRIPTIONS,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_PLAY_VIDEO,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { openLink } from "../../../../../utils/string";
import { useState } from "react";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import PerformingAssetsBento, {
  PerformingAssetsBentoItem,
} from "../components/PerformingAssetsBento";
import PerformingAssetsLeaderboard from "../components/PerformingAssetsLeaderboard";
import { buildPerformingAssetSummary } from "../components/buildPerformingAssetSummary";
import {
  accumulateOptionalMetrics,
  canShowPerformingEntity,
  findCampaignEntityByID,
  finiteMetric,
  formatTranslatedMetric,
  getBestCopies,
  hasAnyPositiveMetric,
  initialOptionalMetrics,
  takeTopPerformers,
  youtubeThumbnailUrl,
  youtubeWatchUrl,
} from "../components/performingAssetsMetrics";
import {
  PERFORMING_ASSETS_EYEBROW_SX,
  PERFORMING_ASSETS_HAIRLINE,
} from "../components/performingAssetsStyles";
import CardMedia from "../../../../../components/Media/CardMedia";
import ModalMedia from "../../../../../components/Modal/ModalMedia";

const GooglePerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);
  // Returns the best assets group, prioritizing mainAction, otherwise by click
  const getBestAssetsGroup = (assetsGroupsStatistics) => {
    if (!assetsGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(assetsGroupsStatistics).forEach(([assetsGroupId, stats]) => {
      if (!stats) return;
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            assetsGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          assetsGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the best ad group, prioritizing mainAction, otherwise by click
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      if (!stats) return;
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the top 5 keywords, prioritizing mainAction, otherwise by click
  const getTop5Keywords = (adsGroups, statistics) => {
    if (!Array.isArray(adsGroups) || !statistics) return [];

    const allKeywords = [];

    adsGroups.forEach((adGroup) => {
      if (adGroup?.platform !== GOOGLE) return;

      const keywordIDs = Array.isArray(adGroup.keywordsIDs)
        ? adGroup.keywordsIDs
        : [];
      const keywords = Array.isArray(adGroup.keywords) ? adGroup.keywords : [];
      const adGroupKeywords = statistics[adGroup.id] || {};

      Object.entries(adGroupKeywords).forEach(([keywordId, keywordStats]) => {
        const keywordIndex = keywordIDs.indexOf(keywordId);
        if (keywordIndex === -1) return;
        const keywordText = keywords[keywordIndex];
        if (!keywordText) return;
        if ((finiteMetric(keywordStats?.impressions) ?? 0) <= 0) return;

        allKeywords.push({
          adGroupId: adGroup.id,
          keywordId,
          keywordText,
          statistics: keywordStats,
        });
      });
    });

    return takeTopPerformers({
      items: allKeywords,
      getStatistics: (item) => item.statistics,
    });
  };

  // Returns the top 5 videos, prioritizing mainAction, otherwise by click
  const getTop5Videos = (videosStatistics) => {
    if (!videosStatistics) return [];

    const videosMap = new Map();

    // Iterate over all assetsGroups and videos
    Object.entries(videosStatistics).forEach(([, videos]) => {
      if (!videos || typeof videos !== "object") return;
      Object.entries(videos).forEach(([, videoStats]) => {
        const youtubeVideoID = videoStats?.youtubeVideoID;
        if (!youtubeVideoID) return;

        // Group videos by youtubeVideoID and sum statistics
        if (videosMap.has(youtubeVideoID)) {
          const existingVideo = videosMap.get(youtubeVideoID);
          accumulateOptionalMetrics({
            target: existingVideo.statistics,
            source: videoStats,
          });
          // Keep the first title found if available
          if (
            !existingVideo.youtubeVideoTitle &&
            videoStats.youtubeVideoTitle
          ) {
            existingVideo.youtubeVideoTitle = videoStats.youtubeVideoTitle;
          }
        } else {
          videosMap.set(youtubeVideoID, {
            youtubeVideoID,
            youtubeVideoTitle: videoStats.youtubeVideoTitle,
            statistics: initialOptionalMetrics(videoStats),
          });
        }
      });
    });

    return takeTopPerformers({
      items: Array.from(videosMap.values()),
      getStatistics: (item) => item.statistics,
      includeItem: (item) => hasAnyPositiveMetric(item.statistics),
    });
  };

  // Returns the top 5 images, prioritizing mainAction, otherwise by click
  const getTop5Images = (imagesStatistics) => {
    if (!imagesStatistics) return [];

    const imagesMap = new Map();

    // Iterate over all assetsGroups and images
    Object.entries(imagesStatistics).forEach(([, images]) => {
      if (!images || typeof images !== "object") return;
      Object.entries(images).forEach(([, imageStats]) => {
        const imageUrl = imageStats?.imageUrl;
        if (!imageUrl) return;

        // Group images by imageUrl and sum statistics
        if (imagesMap.has(imageUrl)) {
          const existingImage = imagesMap.get(imageUrl);
          accumulateOptionalMetrics({
            target: existingImage.statistics,
            source: imageStats,
          });
        } else {
          imagesMap.set(imageUrl, {
            imageUrl,
            statistics: initialOptionalMetrics(imageStats),
          });
        }
      });
    });

    return takeTopPerformers({
      items: Array.from(imagesMap.values()),
      getStatistics: (item) => item.statistics,
      includeItem: (item) => hasAnyPositiveMetric(item.statistics),
    });
  };

  // Returns the top 5 text assets (headlines / long headlines / descriptions)
  const getTop5TextAssets = (textStatistics) => {
    if (!textStatistics) return [];

    const textsMap = new Map();

    Object.entries(textStatistics).forEach(([, texts]) => {
      if (!texts || typeof texts !== "object") return;
      Object.entries(texts).forEach(([assetID, textStats]) => {
        const text = textStats?.text;
        if (!text) return;

        if (textsMap.has(text)) {
          const existing = textsMap.get(text);
          accumulateOptionalMetrics({
            target: existing.statistics,
            source: textStats,
          });
        } else {
          textsMap.set(text, {
            assetID,
            text,
            statistics: initialOptionalMetrics(textStats),
          });
        }
      });
    });

    return takeTopPerformers({
      items: Array.from(textsMap.values()),
      getStatistics: (item) => item.statistics,
      includeItem: (item) => hasAnyPositiveMetric(item.statistics),
    });
  };

  // Returns the best ad, prioritizing mainAction, otherwise by click
  const getBestAd = (adsGroups, statistics) => {
    if (!Array.isArray(adsGroups) || !statistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup?.platform !== GOOGLE || !Array.isArray(adGroup.ads)) return;

      adGroup.ads.forEach((ad) => {
        if (!ad?.id) return;
        const adStats = statistics[ad.id];
        if (!adStats) return;

        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestByMainAction ||
            adStats.mainAction > bestByMainAction.statistics.mainAction
          ) {
            bestByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }
        if (
          !bestByClick ||
          (adStats.click || 0) > (bestByClick.statistics.click || 0)
        ) {
          bestByClick = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  const googleStats = statistics?.[GOOGLE] ?? {};
  const bestAssetsGroup = getBestAssetsGroup(googleStats.assetsGroups);
  const bestAdGroup = getBestAdGroup(googleStats.adsGroups);
  const bestAd = getBestAd(campaign?.adsGroups, googleStats.ads);
  const { bestTitle, bestDescription } = getBestCopies({
    adTexts: googleStats.adTexts,
    adId: bestAd?.adId,
  });
  const top5Keywords = getTop5Keywords(
    campaign?.adsGroups,
    googleStats.keywords
  );
  const top5Videos = getTop5Videos(googleStats.videos);
  const top5Images = getTop5Images(googleStats.images);
  const top5Headlines = getTop5TextAssets(
    googleStats.titles || googleStats.assets?.titles
  );
  const top5LongHeadlines = getTop5TextAssets(
    googleStats.longHeadlines || googleStats.assets?.longHeadlines
  );
  const top5Descriptions = getTop5TextAssets(
    googleStats.descriptions || googleStats.assets?.descriptions
  );

  const getAssetsGroupByID = (id) => {
    const assetsGroup = findCampaignEntityByID({
      entities: campaign?.assetsGroups,
      id,
    });
    if (!assetsGroup?.id) return {};
    return { assetsGroup, assetsGroupID: assetsGroup.id };
  };

  const getAdGroupByID = (id) => {
    const adGroup = findCampaignEntityByID({
      entities: campaign?.adsGroups,
      id,
      platform: GOOGLE,
    });
    if (!adGroup?.id) return {};
    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsGoogle = (campaign?.adsGroups ?? []).filter(
      (adGroup) => adGroup.platform === GOOGLE
    );

    const foundAdGroup = adGroupsGoogle.find(
      (adGroup) =>
        Array.isArray(adGroup?.ads) && adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((item) => item.id === adId);
    if (!ad?.id) return {};
    const indexAd = foundAdGroup.ads.findIndex((item) => item.id === adId);
    const adGroupIndex = adGroupsGoogle.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
      indexAd,
    };
  };

  const { assetsGroup = {}, assetsGroupID } = getAssetsGroupByID(
    bestAssetsGroup?.assetsGroupId
  );
  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const {
    ad = {},
    adID,
    adGroupID: adGroupIDAd,
    indexAd,
  } = getAdByID(bestAd?.adId);

  const summaryLabels = {
    t,
    impressionsLabel: t(IMPRESSIONS),
    ctrLabel: t(CTR),
    clicksLabel: t(CLICKS),
    resultsLabel: t(RESULTS),
    valueNumber: VALUE_NUMBER,
    valuePercentage: VALUE_WITH_PERCENTAGE,
  };

  const showBestAssetsGroup = canShowPerformingEntity({
    entity: assetsGroup,
    statistics: bestAssetsGroup?.statistics,
  });
  const showBestAdGroup = canShowPerformingEntity({
    entity: adGroup,
    statistics: bestAdGroup?.statistics,
  });
  const showBestAd = canShowPerformingEntity({
    entity: ad,
    statistics: bestAd?.statistics,
  });

  const bestAssetsGroupStats = showBestAssetsGroup
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAssetsGroup.statistics,
      })
    : [];

  const bestAdGroupStats = showBestAdGroup
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAdGroup.statistics,
      })
    : [];

  const bestAdStats = showBestAd
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAd.statistics,
      })
    : [];

  const copyContent =
    bestTitle?.text || bestDescription?.text ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {bestTitle?.text ? (
          <Box>
            <Typography component="p" sx={PERFORMING_ASSETS_EYEBROW_SX}>
              {t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_TITLE)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "text.primary" }}>
              {bestTitle.text}
            </Typography>
          </Box>
        ) : null}
        {bestDescription?.text ? (
          <Box>
            <Typography component="p" sx={PERFORMING_ASSETS_EYEBROW_SX}>
              {t(
                ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_DESCRIPTION
              )}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "text.primary" }}>
              {bestDescription.text}
            </Typography>
          </Box>
        ) : null}
      </Box>
    ) : null;

  const textCards = [
    top5Headlines.length > 0 && {
      key: "top5Headlines",
      titleKey: ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_HEADLINES,
      texts: top5Headlines,
    },
    top5LongHeadlines.length > 0 && {
      key: "top5LongHeadlines",
      titleKey: ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_LONG_HEADLINES,
      texts: top5LongHeadlines,
    },
    top5Descriptions.length > 0 && {
      key: "top5Descriptions",
      titleKey: ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_DESCRIPTIONS,
      texts: top5Descriptions,
    },
  ].filter(Boolean);

  const sections = [
    showBestAdGroup && (
      <PerformingAssetsBentoItem key="bestAdGroup" span={6}>
        <PerformingAssetsCard
          platform={GOOGLE}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP)}
          subtitle={adGroup.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&adGroupID=${adGroupID}`
            )
          }
          statistics={bestAdGroupStats}
        />
      </PerformingAssetsBentoItem>
    ),
    top5Keywords.length > 0 && (
      <PerformingAssetsBentoItem key="top5Keywords" span={6}>
        <TopKeywords top5Keywords={top5Keywords} />
      </PerformingAssetsBentoItem>
    ),
    showBestAd && (
      <PerformingAssetsBentoItem key="bestAd" span={12}>
        <PerformingAssetsCard
          platform={GOOGLE}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
          subtitle={ad.name || `${t(AD)} ${(indexAd ?? 0) + 1}`}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&adGroupID=${adGroupIDAd}&adID=${adID}`
            )
          }
          statistics={bestAdStats}
          layout={copyContent ? "split" : "metric"}
          nextContent={copyContent}
        />
      </PerformingAssetsBentoItem>
    ),
    showBestAssetsGroup && (
      <PerformingAssetsBentoItem key="bestAssetsGroup" span={6}>
        <PerformingAssetsCard
          platform={GOOGLE}
          title={t(
            ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_ASSETS_GROUP
          )}
          subtitle={assetsGroup.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&assetsGroupID=${assetsGroupID}`
            )
          }
          statistics={bestAssetsGroupStats}
        />
      </PerformingAssetsBentoItem>
    ),
    top5Images.length > 0 && (
      <PerformingAssetsBentoItem key="top5Images" span={6}>
        <TopImages top5Images={top5Images} />
      </PerformingAssetsBentoItem>
    ),
    top5Videos.length > 0 && (
      <PerformingAssetsBentoItem key="top5Videos" span={12}>
        <TopVideos top5Videos={top5Videos} />
      </PerformingAssetsBentoItem>
    ),
    ...textCards.map((card, index) => {
      const isLastOdd =
        textCards.length % 2 === 1 && index === textCards.length - 1;
      return (
        <PerformingAssetsBentoItem key={card.key} span={isLastOdd ? 12 : 6}>
          <TopTextAssets titleKey={card.titleKey} top5Texts={card.texts} />
        </PerformingAssetsBentoItem>
      );
    }),
  ].filter(Boolean);

  if (!sections.length) return null;

  return <PerformingAssetsBento>{sections}</PerformingAssetsBento>;
};

const countMetrics = ({ statistics, t, includeCtr = false }) => ({
  impressions: formatTranslatedMetric({
    t,
    key: VALUE_NUMBER,
    value: statistics?.impressions,
  }),
  ...(includeCtr
    ? {
        ctr: formatTranslatedMetric({
          t,
          key: VALUE_WITH_PERCENTAGE,
          value: statistics?.ctr,
        }),
      }
    : {}),
  clicks: formatTranslatedMetric({
    t,
    key: VALUE_NUMBER,
    value: statistics?.click,
  }),
  results: formatTranslatedMetric({
    t,
    key: VALUE_NUMBER,
    value: statistics?.mainAction,
  }),
});

const TopKeywords = ({ top5Keywords }) => {
  const { t } = useTranslationApp();
  const columns = [
    { key: "impressions", label: t(IMPRESSIONS) },
    { key: "ctr", label: t(CTR) },
    { key: "clicks", label: t(CLICKS) },
    { key: "results", label: t(RESULTS) },
  ];

  return (
    <PerformingAssetsCard
      platform={GOOGLE}
      title={t(TOP_KEYWORDS)}
      nextContent={
        <PerformingAssetsLeaderboard
          primaryClamp={1}
          columns={columns}
          rows={top5Keywords.map((keyword) => ({
            id: `${keyword.adGroupId}-${keyword.keywordId}`,
            primary: keyword.keywordText,
            metrics: countMetrics({
              statistics: keyword.statistics,
              t,
              includeCtr: true,
            }),
          }))}
        />
      }
    />
  );
};

const TopVideos = ({ top5Videos }) => {
  const { t } = useTranslationApp();
  return (
    <PerformingAssetsCard
      platform={GOOGLE}
      title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_VIDEOS)}
      nextContent={
        <PerformingAssetsLeaderboard
          columns={[
            { key: "impressions", label: t(IMPRESSIONS) },
            { key: "clicks", label: t(CLICKS) },
            { key: "results", label: t(RESULTS) },
          ]}
          rows={top5Videos.map((video) => ({
            id: video.youtubeVideoID,
            primary: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_PLAY_VIDEO)}
                  onClick={() =>
                    openLink(
                      youtubeWatchUrl({
                        youtubeVideoID: video.youtubeVideoID,
                      })
                    )
                  }
                  sx={{
                    position: "relative",
                    width: 112,
                    height: 63,
                    p: 0,
                    border: 0,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "#111",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="img"
                    alt=""
                    src={youtubeThumbnailUrl({
                      youtubeVideoID: video.youtubeVideoID,
                    })}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <PlayArrowIcon
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#fff",
                      fontSize: 28,
                      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.45))",
                    }}
                  />
                </Box>
                {video.youtubeVideoTitle ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {video.youtubeVideoTitle}
                  </Typography>
                ) : null}
              </Box>
            ),
            metrics: countMetrics({ statistics: video.statistics, t }),
          }))}
        />
      }
    />
  );
};

const TopTextAssets = ({ titleKey, top5Texts }) => {
  const { t } = useTranslationApp();
  return (
    <PerformingAssetsCard
      platform={GOOGLE}
      title={t(titleKey)}
      nextContent={
        <PerformingAssetsLeaderboard
          columns={[
            { key: "impressions", label: t(IMPRESSIONS) },
            { key: "clicks", label: t(CLICKS) },
            { key: "results", label: t(RESULTS) },
          ]}
          rows={top5Texts.map((item) => ({
            id: `${item.assetID}-${item.text}`,
            primary: item.text,
            metrics: countMetrics({ statistics: item.statistics, t }),
          }))}
        />
      }
    />
  );
};

const TopImages = ({ top5Images }) => {
  const { t } = useTranslationApp();
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const isPdfExport = document.documentElement.classList.contains("pdf-export");

  const onImageClick = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
  };

  const onClosePreview = () => {
    setPreviewImageUrl(null);
  };

  return (
    <PerformingAssetsCard
      platform={GOOGLE}
      title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_IMAGES)}
      nextContent={
        <Box>
          <PerformingAssetsLeaderboard
            columns={[
              { key: "impressions", label: t(IMPRESSIONS) },
              { key: "clicks", label: t(CLICKS) },
              { key: "results", label: t(RESULTS) },
            ]}
            rows={top5Images.map((image) => ({
              id: image.imageUrl,
              primary: (
                <Box
                  onClick={() => onImageClick(image.imageUrl)}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    flexShrink: 0,
                    cursor: "pointer",
                    border: `1px solid ${PERFORMING_ASSETS_HAIRLINE}`,
                  }}
                >
                  <CardMedia
                    url={image.imageUrl}
                    eager={isPdfExport}
                    sxImage={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ),
              metrics: countMetrics({ statistics: image.statistics, t }),
            }))}
          />
          {previewImageUrl ? (
            <ModalMedia
              modalOpen={!!previewImageUrl}
              onCloseModal={onClosePreview}
              url={previewImageUrl}
              showEditButton={false}
              showShareButton={false}
              showDownloadButton={false}
            />
          ) : null}
        </Box>
      }
    />
  );
};

export default GooglePerformingAssets;
